import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowRightLeft, Plus, RefreshCw, X } from 'lucide-react';
import { getInventoryTransfers, getInventoryTransfer, listRestaurants } from '../services/api';
import HelpIcon from '../components/help/HelpIcon';
import TransferStockModal from '../components/TransferStockModal';
import { FEATURE_GATE_ERROR_CODE } from '../config/featureGates';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import { formatCurrency } from '../utils/formatCurrency';
import {
  normalizeRestaurantList,
  lineMovements,
  formatTransferQuantity,
} from '../utils/inventoryTransfers';
import { storage } from '../utils/storage';
import useEscapeKey from '../hooks/useEscapeKey';
import './InventoryTransfersPage.css';

const PAGE_LIMIT = 100;

const EMPTY_FILTERS = { restaurantId: '', from: '', to: '' };

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const isFeatureGate = (error) =>
  error?.response?.status === 403 && error?.response?.data?.error === FEATURE_GATE_ERROR_CODE;

const selectedRestaurantId = () => {
  try {
    return storage.get('selectedRestaurantId') || '';
  } catch {
    return '';
  }
};

/**
 * Cambio de stock "antes → después" de un movimiento (o de los campos de la línea si no vino).
 * @param {Object|null} movement - Movimiento
 * @param {number} before - Stock antes según la línea
 * @param {number} after - Stock después según la línea
 * @returns {string} Texto
 */
const stockChange = (movement, before, after) =>
  `${formatTransferQuantity(movement ? movement.StockBefore : before)} → ${formatTransferQuantity(
    movement ? movement.StockAfter : after
  )}`;

/**
 * Transferencias entre locales (RES-44, épica D2): historial con filtros por local y fechas,
 * detalle con la salida en el origen y la entrada en el destino de cada línea, y alta de una
 * transferencia nueva. Un admin de sucursal ve solo las suyas (lo filtra el backend).
 * @returns {JSX.Element} Página
 */
function InventoryTransfersPage() {
  const { t } = useTranslation();
  const [transfers, setTransfers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // { kind: 'gate'|'error', error }
  const [modalOpen, setModalOpen] = useState(false);
  const [detail, setDetail] = useState(null); // { transferId, loading, error, transfer }
  const [feedback, setFeedback] = useState('');
  const requestSeq = useRef(0);

  useEscapeKey(Boolean(detail) && !modalOpen, () => setDetail(null));

  useEffect(() => {
    listRestaurants()
      .then((data) => setRestaurants(normalizeRestaurantList(data)))
      .catch(() => setRestaurants([]));
  }, []);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const params = { limit: PAGE_LIMIT };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const data = await getInventoryTransfers(params);
      if (seq !== requestSeq.current) return;
      setTransfers(data?.transfers || []);
      setLoadError(null);
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error('Error loading transfers:', error?.response?.data || error);
      setTransfers([]);
      // El texto se arma al pintar: así sigue al idioma y `load` no depende de `t`
      setLoadError({ kind: isFeatureGate(error) ? 'gate' : 'error', error });
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(''), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const restaurantNames = useMemo(
    () => new Map(restaurants.map((r) => [r.restaurantId, r.name])),
    [restaurants]
  );
  const nameOf = (id, fallback) => fallback || restaurantNames.get(id) || id;

  const setFilter = (name, value) => setFilters((prev) => ({ ...prev, [name]: value }));
  const hasFilters = Boolean(filters.restaurantId || filters.from || filters.to);

  const openDetail = async (transferId) => {
    setDetail({ transferId, loading: true, error: null, transfer: null });
    try {
      const transfer = await getInventoryTransfer(transferId);
      setDetail((prev) =>
        prev && prev.transferId === transferId ? { ...prev, loading: false, transfer } : prev
      );
    } catch (error) {
      console.error('Error loading transfer detail:', error?.response?.data || error);
      const message = inventoryErrorMessage(t, error, 'inventory.transfers.errors.detail');
      setDetail((prev) =>
        prev && prev.transferId === transferId ? { ...prev, loading: false, error: message } : prev
      );
    }
  };

  const handleTransferred = () => {
    setFeedback(t('inventory.transfers.created'));
    load();
  };

  const renderList = () => {
    if (loading && transfers.length === 0) {
      return <p className="transfers-muted">{t('common.loading')}</p>;
    }
    if (loadError) {
      return (
        <div className="transfers-empty" data-testid={`transfers-error-${loadError.kind}`}>
          <p>
            {loadError.kind === 'gate'
              ? t('inventory.transfers.errors.featureGate')
              : inventoryErrorMessage(t, loadError.error, 'inventory.transfers.errors.load')}
          </p>
          {loadError.kind === 'error' && (
            <button type="button" className="btn btn-secondary" onClick={load}>
              <RefreshCw size={16} aria-hidden="true" />
              {t('common.retry')}
            </button>
          )}
        </div>
      );
    }
    if (transfers.length === 0) {
      return (
        <div className="transfers-empty" data-testid="transfers-empty">
          <ArrowRightLeft size={32} aria-hidden="true" />
          <p>
            {hasFilters ? t('inventory.transfers.emptyFiltered') : t('inventory.transfers.empty')}
          </p>
        </div>
      );
    }
    return (
      <div className="transfers-table-wrapper">
        <table className="transfers-table">
          <thead>
            <tr>
              <th>{t('inventory.transfers.columns.date')}</th>
              <th>{t('inventory.transfers.columns.route')}</th>
              <th className="num">{t('inventory.transfers.columns.lines')}</th>
              <th className="num">{t('inventory.transfers.columns.totalCost')}</th>
              <th>{t('inventory.transfers.columns.reason')}</th>
            </tr>
          </thead>
          <tbody>
            {transfers.map((transfer) => (
              <tr
                key={transfer.transferId}
                className="transfers-row"
                onClick={() => openDetail(transfer.transferId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openDetail(transfer.transferId);
                  }
                }}
                tabIndex={0}
                data-testid={`transfer-row-${transfer.transferId}`}
              >
                <td>{formatDateTime(transfer.createdAt)}</td>
                <td>
                  <span className="transfers-route">
                    {nameOf(transfer.fromRestaurantId, transfer.fromRestaurantName)}
                    <ArrowRight size={14} aria-hidden="true" />
                    {nameOf(transfer.toRestaurantId, transfer.toRestaurantName)}
                  </span>
                </td>
                <td className="num">{transfer.lineCount ?? (transfer.lines || []).length}</td>
                <td className="num">
                  {transfer.totalCost === null || transfer.totalCost === undefined
                    ? '—'
                    : formatCurrency(transfer.totalCost)}
                </td>
                <td className="transfers-reason">{transfer.reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDetailBody = () => {
    if (detail.loading) return <p className="transfers-muted">{t('common.loading')}</p>;
    if (detail.error) {
      return (
        <div className="alert alert-error" role="alert">
          {detail.error}
        </div>
      );
    }
    const transfer = detail.transfer || {};
    const fromName = nameOf(transfer.fromRestaurantId, transfer.fromRestaurantName);
    const toName = nameOf(transfer.toRestaurantId, transfer.toRestaurantName);
    return (
      <>
        <dl className="transfers-detail-meta">
          <div>
            <dt>{t('inventory.transfers.columns.date')}</dt>
            <dd>{formatDateTime(transfer.createdAt)}</dd>
          </div>
          <div>
            <dt>{t('inventory.transfers.columns.route')}</dt>
            <dd className="transfers-route">
              {fromName}
              <ArrowRight size={14} aria-hidden="true" />
              {toName}
            </dd>
          </div>
          <div>
            <dt>{t('inventory.transfers.columns.totalCost')}</dt>
            <dd>
              {transfer.totalCost === null || transfer.totalCost === undefined
                ? '—'
                : formatCurrency(transfer.totalCost)}
            </dd>
          </div>
          {transfer.reason && (
            <div>
              <dt>{t('inventory.transfers.columns.reason')}</dt>
              <dd>{transfer.reason}</dd>
            </div>
          )}
        </dl>
        <div className="transfers-table-wrapper">
          <table className="transfers-table transfers-detail-table">
            <thead>
              <tr>
                <th>{t('inventory.transfers.columns.item')}</th>
                <th className="num">{t('inventory.transfers.columns.quantity')}</th>
                <th className="num">
                  {t('inventory.transfers.detail.outAt', { restaurant: fromName })}
                </th>
                <th className="num">
                  {t('inventory.transfers.detail.inAt', { restaurant: toName })}
                </th>
                <th className="num">{t('inventory.transfers.columns.cost')}</th>
              </tr>
            </thead>
            <tbody>
              {(transfer.lines || []).map((line) => {
                const moves = lineMovements(transfer.movements, line.itemId);
                return (
                  <tr key={line.itemId} data-testid={`detail-line-${line.itemId}`}>
                    <td>{line.name || line.itemId}</td>
                    <td className="num">
                      {formatTransferQuantity(line.quantity)} {line.unit || ''}
                    </td>
                    <td
                      className="num transfer-stock-change--out"
                      data-testid={`detail-out-${line.itemId}`}
                    >
                      {stockChange(moves.out, line.sourceStockBefore, line.sourceStockAfter)}
                    </td>
                    <td
                      className="num transfer-stock-change--in"
                      data-testid={`detail-in-${line.itemId}`}
                    >
                      {stockChange(moves.in, line.targetStockBefore, line.targetStockAfter)}
                    </td>
                    <td className="num">
                      {line.cost === null || line.cost === undefined
                        ? '—'
                        : formatCurrency(line.cost)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="transfers-muted transfers-detail-note">
          {t('inventory.transfers.detail.movementsNote')}
        </p>
      </>
    );
  };

  return (
    <div className="inventory-transfers-page">
      <div className="transfers-header">
        <div>
          <h1>
            {t('inventory.transfers.title')}
            <HelpIcon topicKey="inventory.transfers" />
          </h1>
          <p className="transfers-subtitle">{t('inventory.transfers.subtitle')}</p>
        </div>
        {loadError?.kind !== 'gate' && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setModalOpen(true)}
            data-testid="transfers-new"
          >
            <Plus size={16} aria-hidden="true" />
            {t('inventory.transfers.new')}
          </button>
        )}
      </div>

      {feedback && (
        <div className="alert alert-success" role="status">
          {feedback}
        </div>
      )}

      <div className="transfers-filters">
        <div className="form-group">
          <label htmlFor="transfers-restaurant">
            {t('inventory.transfers.filters.restaurant')}
          </label>
          <select
            id="transfers-restaurant"
            value={filters.restaurantId}
            onChange={(e) => setFilter('restaurantId', e.target.value)}
          >
            <option value="">{t('inventory.transfers.filters.allRestaurants')}</option>
            {restaurants.map((r) => (
              <option key={r.restaurantId} value={r.restaurantId}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="transfers-from">{t('inventory.transfers.filters.from')}</label>
          <input
            id="transfers-from"
            type="date"
            value={filters.from}
            max={filters.to || undefined}
            onChange={(e) => setFilter('from', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="transfers-to">{t('inventory.transfers.filters.to')}</label>
          <input
            id="transfers-to"
            type="date"
            value={filters.to}
            min={filters.from || undefined}
            onChange={(e) => setFilter('to', e.target.value)}
          />
        </div>
        {hasFilters && (
          <button
            type="button"
            className="btn btn-ghost btn-sm transfers-clear"
            onClick={() => setFilters(EMPTY_FILTERS)}
          >
            {t('inventory.transfers.filters.clear')}
          </button>
        )}
      </div>

      {renderList()}

      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div
            className="modal-content modal-content--wide transfers-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="transfers-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="transfers-detail-title">{t('inventory.transfers.detail.title')}</h2>
              <button
                type="button"
                className="transfers-close"
                onClick={() => setDetail(null)}
                aria-label={t('common.close')}
              >
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">{renderDetailBody()}</div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setDetail(null)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <TransferStockModal
          restaurants={restaurants.length ? restaurants : undefined}
          initialFromRestaurantId={filters.restaurantId || selectedRestaurantId()}
          onClose={() => setModalOpen(false)}
          onTransferred={handleTransferred}
        />
      )}
    </div>
  );
}

export default InventoryTransfersPage;
